import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToastNotificationsControl } from '@tanium/react-toast-notifications-context';
import { TooltippedIconButton } from '@tanium/react-icon-button';
import { ConfirmModal } from '@tanium/react-confirm-modal';
import { TextInput } from '@tanium/react-text-input';
import { DownloadIcon, TaniumIcon, TrashIcon } from '@tanium/react-graphics';
import {
  StackedRibbonListMinimalStatic,
  StackedRibbonListWrapper,
  RibbonColumn,
  SortChangeEvent,
  SortItem,
} from '@tanium/react-stacked-ribbon-list';
import { Box } from '@tanium/react-box';
import { Page, PageHeader } from '@tanium/react-page';
import { PageSection } from '@tanium/react-page-section';
import { Well } from '@tanium/react-well';
import { TextWithIcon } from '@tanium/react-text-with-icon';
import { Shimmer } from '@tanium/react-shimmer';
import { Notification } from '@tanium/react-persistent-notifications';
import { Stack } from '@tanium/react-stack';
import { DownloadableLicense } from '../../types';
import { alwaysTrueOp, sortData } from '../../utilities/commonFuncs';
import { formatDate, getDateFromDateOnlyString } from '../../utilities/dateHelper';
import { saveBlob } from '../../utilities/downloadHelper';
import { AsyncSuspense } from '../../components/AsyncSuspense';
import { HostnamesCell } from '../../components/HostnamesCell';
import { useDownloadableLicenses } from '../../hooks/useDownloadableLicenses';
import { useModalState } from '../../hooks/useModalState';
import { useTaniumInstances } from '../../hooks/useTaniumInstances';
import { useHasPermissions } from '../../hooks/useUserPermissions';
import { Permission } from '../../models/Permissions';

const EMPTY_CELL = '--';

// An expired license cannot be downloaded, so its row reads as inactive. The ribbon list exposes no
// row-level style hook, so every column dims its own cell.
const EXPIRED_ROW_OPACITY = 0.5;
const dimIfExpired = (license: DownloadableLicense, content: React.ReactNode): React.ReactNode =>
  license.expired ? <span style={{ opacity: EXPIRED_ROW_OPACITY }}>{content}</span> : content;

// A pending offer is not the tenant's to discard while it can still be claimed — downloading it is
// what links the deployment — but once it has expired there is nothing left to claim.
const isRemovable = (license: DownloadableLicense): boolean => !license.isAvailable || Boolean(license.expired);

// A pending offer has no instance yet, so the rows are keyed by license instead.
const licenseRowId = (license: DownloadableLicense): string => String(license.licenseId);

export const LicensesPage: React.VFC = () => {
  const { t } = useTranslation();
  const { showNotification } = useToastNotificationsControl();
  const { downloadableLicenses, loadDownloadableLicenses, downloadLicense, deleteLicense } = useDownloadableLicenses();
  const { loadLinkedTaniumInstances } = useTaniumInstances();
  const canDownloadLicense = useHasPermissions([Permission.DownloadLicense]);
  const canDeleteLicense = useHasPermissions([Permission.DeleteLicense]);

  const [sort, setSort] = useState<SortItem[]>([]);
  const [filter, setFilter] = useState('');
  const [downloadingLicenseId, setDownloadingLicenseId] = useState<number | undefined>();
  const {
    isModalOpen: isRemoveLicenseModalOpen,
    openModal: openRemoveLicenseModal,
    closeModal: closeRemoveLicenseModal,
    data: licenseToRemove,
  } = useModalState<DownloadableLicense>();
  const [isRemoving, setIsRemoving] = useState(false);

  const location = useLocation();
  const history = useHistory();
  useEffect(() => {
    const seeded = new URLSearchParams(location.search).get('filter');
    if (seeded) {
      setFilter(seeded);
      history.replace({ search: '' });
    }
  }, []);

  const sortedLicenses = useMemo(() => sortData(sort, downloadableLicenses), [downloadableLicenses, sort]);
  const filteredLicenses = useMemo(() => {
    const query = filter.trim().toLowerCase();
    if (!query) return sortedLicenses;
    return sortedLicenses.filter((license) => {
      const fields = [
        license.customerName,
        license.url,
        license.hostnames,
        license.friendlyName,
        license.licenseId,
        license.sdrId,
      ];
      return fields.some((field) => field != null && String(field).toLowerCase().includes(query));
    });
  }, [sortedLicenses, filter]);

  const onSortChange = useCallback(({ sort: nextSort }: SortChangeEvent) => {
    setSort(nextSort);
  }, []);

  const handleDownload = useCallback(
    async (license: DownloadableLicense) => {
      setDownloadingLicenseId(license.licenseId);
      try {
        const blob = await downloadLicense(license.licenseId);
        saveBlob(blob, 'tanium.license');
        // Downloading a pending license links its instance to the tenant, so the instances the rest of
        // the app renders are now out of date.
        if (license.isAvailable) {
          await loadLinkedTaniumInstances?.execute();
        }
        showNotification({ type: 'success', contents: t('License downloaded') });
      } catch {
        // A failed fetch on a pending license leaves the offer intact (claim not burned); an already
        // linked license is unaffected.
        showNotification({
          type: 'error',
          contents: license.isAvailable
            ? t('Failed to download the license. The instance is still available — please try again.')
            : t('Failed to download the license. Please try again.'),
        });
      } finally {
        setDownloadingLicenseId(undefined);
      }
    },
    [downloadLicense, loadLinkedTaniumInstances, showNotification, t]
  );

  const downloadTooltip = useCallback(
    (license: DownloadableLicense): string => {
      if (!canDownloadLicense) {
        return t('You have insufficient permissions to download this license.');
      }
      if (license.expired) {
        return t('This license has expired and can no longer be downloaded.');
      }
      return license.isAvailable ? t('Download license and link this instance') : t('Download license');
    },
    [canDownloadLicense, t]
  );

  const handleRemove = useCallback(
    async (license: DownloadableLicense) => {
      setIsRemoving(true);
      try {
        await deleteLicense(license.licenseId);
        showNotification({ type: 'success', contents: t('License removed') });
      } catch {
        showNotification({ type: 'error', contents: t('Failed to remove the license. Please try again.') });
      } finally {
        setIsRemoving(false);
        closeRemoveLicenseModal();
      }
    },
    [deleteLicense, closeRemoveLicenseModal, showNotification, t]
  );

  const removeTooltip = useCallback(
    (license: DownloadableLicense): string => {
      if (!canDeleteLicense) {
        return t('You have insufficient permissions to remove this license.');
      }
      if (!isRemovable(license)) {
        return t('A pending license offer cannot be removed until it expires.');
      }
      return t('Remove license');
    },
    [canDeleteLicense, t]
  );

  const columns: RibbonColumn<DownloadableLicense>[] = useMemo(
    () => [
      {
        label: t('Customer'),
        dataKey: 'customerName',
        grow: 1,
        isSortDisabled: false,
        renderer: ({ itemData }) =>
          dimIfExpired(itemData, <span style={{ fontWeight: 'bold' }}>{itemData.customerName || EMPTY_CELL}</span>),
      },
      {
        label: t('Hostname'),
        // dataKey stays 'url' so sorting keeps following the underlying field.
        dataKey: 'url',
        grow: 2,
        noWrap: false,
        isSortDisabled: false,
        // A pending offer has no linked instance to take a URL from, so it falls back to the
        // hostnames the offered license carries. Either source can be a list, so both truncate for
        // display.
        renderer: ({ itemData }) =>
          dimIfExpired(itemData, <HostnamesCell hostnames={itemData.url || itemData.hostnames} />),
      },
      {
        label: t('License ID'),
        dataKey: 'licenseId',
        grow: 1,
        isSortDisabled: false,
        renderer: ({ itemData }) => dimIfExpired(itemData, itemData.licenseId),
      },
      {
        label: t('License Expiration Date'),
        dataKey: 'expiresAt',
        grow: 1,
        isSortDisabled: false,
        renderer: ({ itemData }) =>
          dimIfExpired(
            itemData,
            itemData.expiresAt ? formatDate(getDateFromDateOnlyString(itemData.expiresAt)) : EMPTY_CELL
          ),
      },
      {
        label: t('Offered'),
        dataKey: 'notifiedAt',
        grow: 1,
        isSortDisabled: false,
        renderer: ({ itemData }) =>
          dimIfExpired(itemData, itemData.notifiedAt ? formatDate(new Date(itemData.notifiedAt)) : EMPTY_CELL),
      },
      {
        label: t('Last Downloaded'),
        dataKey: 'downloadedAt',
        grow: 1,
        isSortDisabled: false,
        renderer: ({ itemData }) =>
          dimIfExpired(itemData, itemData.downloadedAt ? formatDate(new Date(itemData.downloadedAt)) : EMPTY_CELL),
      },
      {
        label: t('Actions'),
        dataKey: 'actions',
        width: 120,
        isSortDisabled: true,
        alignment: 'right' as const,
        renderer: ({ itemData }) => (
          <Stack direction="row" itemSpacing={1} alignItems="center" inline justifyContent="flex-end">
            {dimIfExpired(
              itemData,
              <TooltippedIconButton
                icon={DownloadIcon}
                label={t('Download license')}
                tooltipContent={downloadTooltip(itemData)}
                disabled={
                  !canDownloadLicense || Boolean(itemData.expired) || downloadingLicenseId === itemData.licenseId
                }
                width={16}
                height={16}
                onClick={() => handleDownload(itemData)}
              />
            )}
            {/* Not dimmed with the rest of an expired row: removing it is the point. */}
            <TooltippedIconButton
              icon={TrashIcon}
              label={t('Remove license')}
              tooltipContent={removeTooltip(itemData)}
              disabled={!canDeleteLicense || !isRemovable(itemData) || isRemoving}
              width={16}
              height={16}
              onClick={() => openRemoveLicenseModal(itemData)}
            />
          </Stack>
        ),
      } as RibbonColumn<DownloadableLicense>,
    ],
    [
      t,
      canDownloadLicense,
      downloadingLicenseId,
      downloadTooltip,
      handleDownload,
      canDeleteLicense,
      isRemoving,
      removeTooltip,
      openRemoveLicenseModal,
    ]
  );

  const dataGetter = useCallback((i: number): DownloadableLicense => filteredLicenses[i], [filteredLicenses]);
  const itemIdGetter = useCallback((i: number) => licenseRowId(filteredLicenses[i]), [filteredLicenses]);

  return (
    <Page header={<PageHeader icon={DownloadIcon} heading={t('Licenses')} />}>
      <Well p={1}>
        <PageSection
          title={<TextWithIcon leadingIcon={TaniumIcon}>{t('On-Premise License Downloads')}</TextWithIcon>}
          additionalTitleContent={
            <Stack width="100%" direction="row" justifyContent="right" alignItems="center" itemSpacing={2}>
              <TextInput
                aria-label={t('Filter licenses')}
                placeholder={t('Filter licenses...')}
                value={filter}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilter(e.target.value)}
                style={{ width: '200px' }}
              />
            </Stack>
          }
        >
          <AsyncSuspense
            state={loadDownloadableLicenses}
            loading={
              <Box mt={2} height={1} width="100%">
                <Shimmer />
              </Box>
            }
            failure={t('Failed to load licenses')}
          >
            <div>
              {filteredLicenses.length ? (
                <StackedRibbonListWrapper>
                  <StackedRibbonListMinimalStatic
                    columns={columns}
                    dataGetter={dataGetter}
                    isItemLoaded={alwaysTrueOp}
                    itemCount={filteredLicenses.length}
                    itemIdGetter={itemIdGetter}
                    sort={sort}
                    onSortChange={onSortChange}
                  ></StackedRibbonListMinimalStatic>
                </StackedRibbonListWrapper>
              ) : (
                <Notification role="note" type="info">
                  {/* Gated on the unfiltered list: a filter that matches nothing means "no match",
                      not "you have no licenses". */}
                  {downloadableLicenses.length
                    ? t('No licenses match the current filter.')
                    : t('No on-premise licenses are available for download.')}
                </Notification>
              )}
            </div>
          </AsyncSuspense>
        </PageSection>
      </Well>
      <ConfirmModal
        isOpen={isRemoveLicenseModalOpen}
        header={t('Remove License')}
        confirmLabel={t('Remove')}
        cancelLabel={t('Cancel')}
        isConfirmDisabled={isRemoving}
        onConfirm={async () => {
          // licenseToRemove is always set here: onConfirm is only reachable through the trash
          // icon's openModal(itemData) call.
          await handleRemove(licenseToRemove!);
        }}
        onCancel={closeRemoveLicenseModal}
      >
        {/* ConfirmModal's children render regardless of isOpen, so licenseToRemove may be undefined
            here before the trash icon has ever been clicked. */}
        <span>
          {t('This will remove license licenseId from your Licenses list. This action cannot be undone.', {
            licenseId: licenseToRemove?.licenseId,
          })}
        </span>
      </ConfirmModal>
    </Page>
  );
};
